import { GroupManager } from './../../../../social/social-pixi-canvas/group-manager';
import { Component, OnInit, OnDestroy, QueryList, ViewChildren } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatIconRegistry } from '@angular/material/icon';
import { MatRadioChange } from '@angular/material/radio';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { MatSliderChange } from '@angular/material/slider';
import { DomSanitizer, SafeStyle } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { NgbCarousel } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngrx/store';
import { ToastrService } from 'ngx-toastr';
import { onlyInLeft } from 'PreciateUtil';
import { ReplaySubject, filter, firstValueFrom, takeUntil, tap } from 'rxjs';

import { LaunchDarklyFlags } from 'app/core/services/launch-darkly/launch-darkly.types';
import { coreSelectors, getFeatureFlagObservable } from 'app/core/state';
import { SocialThemeTab } from 'app/modules/social/modal-create-social/modal-create-social.types';
import {
  ModalSimpleSocialComponent,
  ModalSimpleSocialData,
} from 'app/modules/social/modal-simple-social/modal-simple-social.component';
import { NONE_PLAYLIST } from 'app/modules/social/services/playlist.service';
import { VideoChatService } from 'app/modules/social/services/videochat.service';
import { MiniMapState } from 'app/modules/social/social-mini-map/mini-map-state';
import { SocialPageService } from 'app/modules/social/social-page/services/social-page.service';
import {
  SocialSetSettingsType,
  SocialSettings,
  SocialSettingsType,
} from 'app/modules/social/social-settings/social-settings.type';
import { actions, socialAppSelectors } from 'app/modules/social/state/app';
import { socialHomePageActions } from 'app/modules/social/state/home-page';
import {
  isHomePageLoading,
  socialSettingsHasError,
  socialSettingsSuccess,
} from 'app/modules/social/state/home-page/selectors';
import { Loggable } from 'app/utils/logging/loggable';
import { notNil } from 'app/utils/stream-util';
import AlternateHost from 'entity/AlternateHost';
import Playlist from 'entity/Playlist';
import Social from 'entity/Social';
import {
  ANYONE_CAN_CHANGE_MUSIC,
  ANYONE_CAN_PRESENT,
  ANYONE_CAN_SHARE,
  ANYONE_CAN_SHARE_TO_GROUP,
  CONVO,
  MAX_GROUP_SIZE,
  MINIMAP_MODE,
  PHOTO_BOOTH,
  SHARE_LINK,
  TOAST,
} from 'entity/SocialSettings';
import SocialTheme from 'entity/SocialTheme';
import User from 'entity/User';
import SocialPermissionType from 'enums/SocialPermissionType';
import Paging from 'rest/Paging';

import { ScootaversePageService } from './../../../services/scootaverse-page.service';
import { DEFAULT_GROUP_SIZE } from 'app/modules/social/social-pixi-canvas/group-manager';

interface RoomSizeSettings {
  value: { size: number; maxParticipants: number };
  label: string;
  checked: boolean;
}

type OldRoomSizeSettings = RoomSizeSettings;
type NewRoomSizeSettings = RoomSizeSettings;

export const newRoomSizeSettings: NewRoomSizeSettings[] = [
  { value: { size: 2000, maxParticipants: 10 }, label: 'Mini - maximum 10 attendees', checked: false },
  { value: { size: 4000, maxParticipants: 25 }, label: 'Small - maximum 25 attendees', checked: false },
  { value: { size: 8000, maxParticipants: 75 }, label: 'Medium - maximum 75 attendees', checked: false },
  {
    value: { size: 12000, maxParticipants: 125 },
    label: 'Large - maximum 125 attendees',
    checked: false,
  },
  {
    value: { size: 16000, maxParticipants: 200 },
    label: 'Extra Large - maximum 200 attendees',
    checked: false,
  },
  {
    value: { size: 20000, maxParticipants: 300 },
    label: 'Jumbo -  maximum 300 attendees',
    checked: false,
  },
];

export const oldRoomSizeSettings: OldRoomSizeSettings[] = [
  { value: { size: 4000, maxParticipants: 25 }, label: 'Small - maximum 25 attendees', checked: false },
  {
    value: { size: 12000, maxParticipants: 50 },
    label: 'Medium - maximum 50 attendees',
    checked: false,
  },
  { value: { size: 16000, maxParticipants: 75 }, label: 'Large - maximum 75 attendees', checked: false },
  {
    value: { size: 20000, maxParticipants: 300 },
    label: 'Extra Large - maximum 300 attendees',
    checked: false,
  },
];

@Component({
  selector: 'room-advanced-settings',
  templateUrl: './room-advanced-settings.component.html',
  styleUrls: ['./room-advanced-settings.component.scss'],
  standalone: false,
})
export class RoomAdvancedSettingsComponent extends Loggable implements OnInit, OnDestroy {
  public socialSettingsFormGroup?: UntypedFormGroup;
  public panelGeneralOpened?: boolean;
  public panelParticipantOpened?: boolean;
  public panelJoinOpened?: boolean;
  public panelAlternateOpened?: boolean;
  public themeTabs: SocialThemeTab[] = [];
  public currentTabIndex?: number;
  public playlistOptions?: Playlist[];
  public playlistSelectedName?: string;
  public social?: Social;
  public themeOptions: SocialTheme[] = [];
  public socialPermissionTypeEnum = SocialPermissionType;
  public loading?: boolean;
  public editMode?: boolean;
  public alternateHostsEnabled = false;
  private geoMusicAllowed?: boolean;
  public showHostLicenseError = false;
  public enableGroupScreenShare = false;
  public panelRoomSizeOpened = false;

  private destroyed$ = new ReplaySubject<boolean>(1);
  private currentAlternateHosts: User[] = [];
  private selectedAlternateHosts: User[] = [];

  public groupSize = 10;
  public minimapMode = MiniMapState.EXPANDED;
  public socialIsOpen = false;
  private maxParticipants = 10;

  /* LD Feature Flags */
  public hostAdmissionEnabled?: boolean;
  public newSideMenu = true;
  public groupSizeEnabled = false;
  public groupSoftCap = false;

  public loadingSocial = false;

  @ViewChildren('themeCarousel') public themeCarousel?: QueryList<NgbCarousel>;
  public minimapModes = [
    { value: MiniMapState.CLOSED, label: 'Closed' },
    { value: MiniMapState.OPENED, label: 'Opened' },
    { value: MiniMapState.EXPANDED, label: 'Expanded' },
  ];

  public roomSizeSettings = oldRoomSizeSettings;
  private roomSize?: number = 4000;

  public constructor(
    private domSanitizer: DomSanitizer,
    private matIconRegistry: MatIconRegistry,
    private fb: UntypedFormBuilder,
    private videoChatService: VideoChatService,
    private route: ActivatedRoute,
    private router: Router,
    private socialPageService: SocialPageService,
    private scootaversePageService: ScootaversePageService,
    private _toasterService: ToastrService,
    private dialog: MatDialog,
    private store: Store,
  ) {
    super();
    this.store.dispatch(socialHomePageActions.RESET_SOCIAL_PAGE());
    this.loadingSocial = true;

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroyed$),
      )
      .subscribe(() => {
        const socialId = this.route.snapshot.paramMap.get('room-id');
        this.editMode = !!this.route.snapshot.queryParamMap.get('edit-mode');

        if (!socialId) {
          this.error('No social id informed. Returning to home');
          void this.router.navigate([`home/scootaverses`]);
          return;
        } else {
          firstValueFrom(this.videoChatService.getSocial(socialId, false))
            .then((social) => {
              this.social = social;
              this.socialIsOpen = this.social?.isOpen ?? false;
              this.store.dispatch(actions.LOAD_SOCIAL({ social: social }));
              void this.loadInitialInformation();
              this.loadingSocial = false;
            })
            .catch((err) => {
              this.error('Error loading social information: ', err);
              void this.router.navigate(['']);
            });
        }
      });

    this.store
      .select(isHomePageLoading)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isLoading) => (this.loading = isLoading));

    this.store
      .select(socialSettingsSuccess)
      .pipe(
        filter((success) => success),
        takeUntil(this.destroyed$),
      )
      .subscribe(() => {
        this._toasterService.success('Room settings saved successfully!', 'Success');
        void this.navigateBack(this.social);
      });

    this.store
      .select(socialSettingsHasError)
      .pipe(
        filter((error) => error),
        takeUntil(this.destroyed$),
      )
      .subscribe(() =>
        this.displayModalMessage(
          'Unable to Save Your Settings',
          'We are unable to save your settings at the moment. Please try again later.',
        ),
      );

    this.store
      .select(socialAppSelectors.getAlternateHosts)
      .pipe(
        tap((hosts) => {
          this.currentAlternateHosts = hosts;
          this.selectedAlternateHosts = hosts;
        }),
        takeUntil(this.destroyed$),
      )
      .subscribe();

    this.store
      .select(coreSelectors.getGeoMusicAllowed)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((allowed) => (this.geoMusicAllowed = allowed));

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.social_new_side_menu)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((newSideMenu) => (this.newSideMenu = newSideMenu));

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.social_host_admission)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((hostAdmissionEnabled) => (this.hostAdmissionEnabled = hostAdmissionEnabled));

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.social_alternate_hosts)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((alternateHostsEnabled) => (this.alternateHostsEnabled = alternateHostsEnabled));

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.social_set_group_size)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((groupSizeEnabled) => (this.groupSizeEnabled = groupSizeEnabled));

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.enable_group_screen_share)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((enableGroupScreenShare) => (this.enableGroupScreenShare = enableGroupScreenShare));

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.enable_new_room_sizes)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((enableNewRoomSizes) => {
        if (enableNewRoomSizes) {
          this.roomSizeSettings = newRoomSizeSettings;
          return;
        }
        this.roomSizeSettings = oldRoomSizeSettings;
      });

    getFeatureFlagObservable(store, LaunchDarklyFlags.group_soft_cap)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((groupSoftCap) => {
        this.groupSoftCap = groupSoftCap;
      });
  }

  public ngOnInit(): void {
    this.store
      .select(socialAppSelectors.isHost)
      .pipe(
        filter((isHost) => !isHost),
        takeUntil(this.destroyed$),
      )
      .subscribe(() => {
        if (!this.loadingSocial) {
          void this.router.navigate(['']);
        }
      });

    // Listen for playlist changes
    this.socialSettingsFormGroup
      ?.get('playlistId')
      ?.valueChanges.pipe(takeUntil(this.destroyed$))
      .subscribe((newPlaylistId) => {
        this.playlistSelectedName = this.getPlaylistNameById(newPlaylistId);
      });
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
    this.store.dispatch(socialHomePageActions.RESET_SOCIAL_PAGE());
  }

  public async getSocialSettings(): Promise<void> {
    const socialId = this.getSocialId();
    if (!socialId) {
      return;
    }

    const { setSettings, settings } = await this.fetchSettingsFromService(socialId);
    this.updateFormGroupWithSettings(settings, setSettings);
    this.configureGroupSize(settings);
    this.configureMinimapMode(settings);
  }

  private getSocialId(): string | undefined {
    return this.social?.socialId;
  }

  private async fetchSettingsFromService(socialId: string): Promise<Partial<SocialSettings>> {
    const { setSettings, settings } = await this.socialPageService.getRoomSettings(socialId);
    return { setSettings, settings };
  }

  private loadFieldsBySocial() {
    this.socialSettingsFormGroup = this.fb.group({
      roomName: [this.social?.name, Validators.required],
      playlistId: [this.social?.playlistId, Validators.required],
      themeId: [this.social?.themeId, Validators.required],
      canToast: [false],
      canConvo: [false],
      canShareLink: [false],
      canStartPhotoBooth: [false],
      anyoneCanPresent: [false],
      anyoneCanShare: [false],
      anyoneCanShareToGroup: [false],
      anyoneCanChangeMusic: [false],
      socialPermissionType: [SocialPermissionType.PUBLIC],
      anyoneCanStart: [this.social?.anyoneCanStart],
      hostAdmitted: [this.social?.hostAdmitted],
      recordMeeting: [false],
    });
  }

  private async loadInitialInformation() {
    this.loadFieldsBySocial();
    await this.getPlaylists();
    await this.getSocialSettings();
    this.getRoomSettings();
  }

  private updateFormGroupWithSettings(
    settings?: Partial<SocialSettingsType>,
    setSettings?: Partial<SocialSetSettingsType>,
  ) {
    const mappings = [
      { formKey: 'canToast', settingKey: TOAST },
      { formKey: 'canConvo', settingKey: CONVO },
      { formKey: 'canShareLink', settingKey: SHARE_LINK },
      { formKey: 'canStartPhotoBooth', settingKey: PHOTO_BOOTH },
      { formKey: 'anyoneCanPresent', settingKey: ANYONE_CAN_PRESENT },
      { formKey: 'anyoneCanShare', settingKey: ANYONE_CAN_SHARE },
      { formKey: 'anyoneCanShareToGroup', settingKey: ANYONE_CAN_SHARE_TO_GROUP },
      { formKey: 'anyoneCanChangeMusic', settingKey: ANYONE_CAN_CHANGE_MUSIC },
      { formKey: 'recordMeeting', settingKey: 'RECORD' },
    ];

    mappings.forEach((map) => {
      if (map.formKey === 'recordMeeting') {
        this.socialSettingsFormGroup?.get(map.formKey)?.patchValue(!!setSettings?.[map.settingKey]);
      } else {
        this.socialSettingsFormGroup?.get(map.formKey)?.patchValue(!!settings?.[map.settingKey]);
      }
    });
  }

  private configureGroupSize(settings?: Partial<SocialSettingsType>) {
    const groupSize = settings?.[MAX_GROUP_SIZE];
    GroupManager.getConfiguredGroupSize(this.groupSoftCap, groupSize);
  }

  private configureMinimapMode(settings?: Partial<SocialSettingsType>) {
    const minimapMode = settings?.[MINIMAP_MODE];
    this.minimapMode = typeof minimapMode !== 'number' ? MiniMapState.OPENED : minimapMode;
  }

  public onGroupSizeChange(event: MatSliderChange): void {
    this.groupSize = Number(event.value);
  }

  public minimapModeChange(mode: MatRadioChange): void {
    this.minimapMode = mode.value;
  }

  public getRoomSettings(): void {
    this.roomSizeSettings.map((a) => {
      a.checked = false;
      if (this.social?.startingWidth === a.value.size) {
        a.checked = true;
        this.roomSize = a.value.size;
        this.maxParticipants = a.value.maxParticipants;
      }
    });
  }

  public roomSizeChange(setting: MatRadioChange): void {
    this.roomSize = setting.value.size;
    this.maxParticipants = setting.value.maxParticipants;
  }

  public async getPlaylists(): Promise<void> {
    const paging = new Paging(0, 100);
    const playlists = (await this.videoChatService.getAllPlaylists(paging)) ?? [];

    playlists.sort((a, b) => {
      if (a.type === undefined) {
        return 1;
      }
      if (b.type === undefined) {
        return -1;
      }

      return a.type > b.type ? -1 : a.type < b.type ? 1 : 0;
    });

    this.playlistOptions = [NONE_PLAYLIST, ...playlists];
    if (!this.social?.playlistId) {
      return;
    }
    this.playlistSelectedName = this.getPlaylistNameById(this.social?.playlistId);
  }

  public getPlaylistNameById(playlistId: string): string {
    const playlistSelectedFound = this.playlistOptions?.find((playlist) => playlist.playlistId === playlistId);
    return playlistSelectedFound?.name ?? '-';
  }

  public safeStyleAttribute(v: string): SafeStyle {
    return v ? this.domSanitizer.bypassSecurityTrustStyle(v) : 'none';
  }

  // ngb-carousel slide listener
  public onSlide(theme: SocialTheme): void {
    this.socialSettingsFormGroup?.get('themeId')?.setValue(theme.socialThemeId);
  }

  public displayModalMessage(modalTitle: string, text: string): void {
    const data = { modalTitle, text, primaryButtonText: 'Close' };
    this.dialog.open<ModalSimpleSocialComponent, ModalSimpleSocialData>(ModalSimpleSocialComponent, {
      panelClass: 'social-modal',
      data,
    });
  }

  public async navigateBack(social?: Social): Promise<void> {
    if (!social) {
      await this.router.navigate(['']);
      return;
    }
    if (this.editMode) {
      await this.scootaversePageService.onEditRoom(social);
    } else {
      await this.router.navigate([`create/${social.socialId}`]);
    }
  }

  public onToggleRequireHostAdmission($event: MatSlideToggleChange): void {
    if ($event.checked) {
      this.socialSettingsFormGroup?.get('anyoneCanStart')?.setValue(false);
    } else {
      this.socialSettingsFormGroup?.get('anyoneCanStart')?.setValue(true);
    }
  }

  public onToggleAllowBeforeHost($event: MatSlideToggleChange): void {
    if ($event.checked) {
      this.socialSettingsFormGroup?.get('hostAdmitted')?.setValue(false);
    } else {
      this.socialSettingsFormGroup?.get('hostAdmitted')?.setValue(true);
    }
  }

  public onAlternateHostsChange(alternateHosts: AlternateHost[]): void {
    this.showHostLicenseError = alternateHosts.some((a) => !a.licensed);
    this.selectedAlternateHosts = alternateHosts.map((a) => a.user).filter(notNil);
  }

  public save(): void {
    if (!this.social?.socialId) {
      return;
    }
    const socialId = this.social?.socialId;

    // Update Room Entity
    this.updateRoomEntity();

    // Update Social Settings
    this.updateSocialSettings(socialId);

    // Update Alternate Hosts
    this.updateAlternateHosts(socialId);
  }

  // Method to Update Room Entity
  private updateRoomEntity(): void {
    const newPlaylistID = this.socialSettingsFormGroup?.get('playlistId')?.value;
    const room = {
      ...this.social,
      name: this.socialSettingsFormGroup?.get('roomName')?.value,
      socialPermissionType: this.socialSettingsFormGroup?.get('socialPermissionType')?.value,
      themeId: this.socialSettingsFormGroup?.get('themeId')?.value,
      playlistId: newPlaylistID === 'null' ? null : newPlaylistID,
      anyoneCanStart: this.socialSettingsFormGroup?.get('anyoneCanStart')?.value,
      hostAdmitted: this.socialSettingsFormGroup?.get('hostAdmitted')?.value,
      startingWidth: this.roomSize,
      startingHeight: this.roomSize,
      maxParticipants: this.maxParticipants,
    };
    this.store.dispatch(socialHomePageActions.EDIT_ROOM_REQ({ room }));
  }

  // Method to Update Social Settings
  private updateSocialSettings(socialId: string): void {
    const socialSettings: SocialSettingsType = {
      TOAST: this.socialSettingsFormGroup?.get('canToast')?.value,
      CONVO: this.socialSettingsFormGroup?.get('canConvo')?.value,
      SHARE_LINK: this.socialSettingsFormGroup?.get('canShareLink')?.value,
      PHOTO_BOOTH: this.socialSettingsFormGroup?.get('canStartPhotoBooth')?.value,
      ANYONE_CAN_PRESENT: this.socialSettingsFormGroup?.get('anyoneCanPresent')?.value,
      ANYONE_CAN_SHARE: this.socialSettingsFormGroup?.get('anyoneCanShare')?.value,
      ANYONE_CAN_SHARE_TO_GROUP: this.socialSettingsFormGroup?.get('anyoneCanShareToGroup')?.value,
      ANYONE_CAN_CHANGE_MUSIC: this.socialSettingsFormGroup?.get('anyoneCanChangeMusic')?.value,
      MINIMAP_MODE: this.minimapMode,
      MAX_GROUP_SIZE: GroupManager.getAdjustedGroupSize(this.groupSoftCap, this.groupSize),
    };

    const setSettings = {
      RECORD: this.socialSettingsFormGroup?.get('recordMeeting')?.value,
    };

    const settings: SocialSettings = { settings: socialSettings, setSettings: setSettings };

    this.store.dispatch(socialHomePageActions.SAVE_SOCIAL_SETTINGS_REQ({ settings: settings, roomId: socialId }));
  }

  // Method to Update Alternate Hosts
  private updateAlternateHosts(socialId: string): void {
    const removedHosts = onlyInLeft(
      this.currentAlternateHosts,
      this.selectedAlternateHosts,
      (a, b) => a.userId === b.userId,
    );
    const addedHosts = onlyInLeft(
      this.selectedAlternateHosts,
      this.currentAlternateHosts,
      (a, b) => a.userId === b.userId,
    );
    const alternateHosts: AlternateHost[] = [
      ...removedHosts.map((h) => ({
        user: h,
        host: false,
      })),
      ...addedHosts.map((h) => ({
        user: h,
        host: true,
      })),
    ];
    this.store.dispatch(
      socialHomePageActions.UPDATE__ALTERNATE_HOSTS_CANDIDATES_REQ({ socialId: socialId, alternateHosts }),
    );
  }
}
